import { datadogRum } from '@datadog/browser-rum';

interface StorageOptions {
    sessionStorageOnly?: boolean;
}

export function getItemFromStorage(
    key: string,
    defaultValue: any,
    { sessionStorageOnly = false }: StorageOptions = {}
): any {
    let value = null;

    // First, attempt to get from sessionStorage
    if (sessionStorage?.getItem) {
        const sessionStorageValue = sessionStorage.getItem(key);
        if (sessionStorageValue !== null) {
            value = sessionStorageValue;
        }
    }

    // If value is null and sessionStorageOnly is false,
    // try to get from localStorage
    if (!sessionStorageOnly && value === null && localStorage?.getItem) {
        const localStorageValue = localStorage.getItem(key);
        if (localStorageValue !== null) {
            value = localStorageValue;
        }
    }

    // If value is still null, return the default value
    if (value === null) {
        value = defaultValue;
    }

    try {
        return JSON.parse(value);
    } catch (error) {
        return value;
    }
}

export function setItemInStorage(
    key: string,
    value: any,
    { sessionStorageOnly = false }: StorageOptions = {}
): void {
    const valueToStore = typeof value === 'string' ? value : JSON.stringify(value);

    try {
        // First, attempt to set in sessionStorage
        sessionStorage?.setItem(key, valueToStore);
    } catch (error) {
        if (sessionStorageOnly) {
            handleSetStorageError(error);
        } else {
            try {
                // If failed to set in sessionStorage, set in localStorage
                localStorage?.setItem(key, valueToStore);
            } catch (error) {
                handleSetStorageError(error);
            }
        }
    }
}

function handleSetStorageError(error: any) {
    if (error instanceof DOMException) {
        if (error.name === 'QuotaExceededError' || error.name === 'NS_ERROR_DOM_QUOTA_REACHED') {
            datadogRum.addError(error, { message: 'Storage quota exceeded' });
        } else {
            datadogRum.addError(error, { message: 'Error setting value in storage' });
        }
    }

    // If failed to set, throw the error
    throw error;
}

export function removeItemFromStorage(
    key: string,
    { sessionStorageOnly = false }: StorageOptions = {}
): void {
    if (sessionStorage?.removeItem) {
        sessionStorage.removeItem(key);
    }

    if (!sessionStorageOnly && localStorage?.removeItem) {
        localStorage.removeItem(key);
    }
}
